/**
 * Landing page.
 *
 * Stands in for rent_management_app/lib/screens/public/landing_screen.dart
 * (hero + featured properties + agents sections). Reachable logged out; an
 * authenticated visitor is redirected to their role home by
 * RedirectIfAuthenticated, matching app_router.dart:84-90.
 *
 * Featured properties below the hero reuse listPublicProperties (the same
 * call PublicPropertyBrowsePage.tsx makes) and the same elevated horizontal
 * card treatment (.featured-property-card, styles.css) that page also uses —
 * kept consistent across both public-facing listing surfaces. This is a
 * logged-out marketing page, so a failed fetch (or zero results) renders
 * nothing extra rather than an error banner — it must never block someone
 * from finding Sign in above.
 *
 * The hero search bar is a client-side redirect into
 * PublicPropertyBrowsePage's own filters (via query params) rather than a
 * second copy of the filtering logic — see that page for how the params are
 * read back on load.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bath, BedDouble, Building2, DoorOpen, MapPin, Maximize, MessageCircle } from 'lucide-react';
import { ROUTES } from '../../routes/paths';
import { listPublicProperties } from '../../api/endpoints/properties';
import { PROPERTY_TYPES, type PropertyType } from '../../types/property';
import {
  deriveBathroomRange,
  deriveBedroomRange,
  deriveFromPrice,
  deriveTotalSquareFeet,
  formatBathroomRange,
  formatBedroomRange,
  formatKes,
  isRecentlyListed,
} from '../../lib/propertyListing';
import { PropertyPicture } from '../../components/PropertyPicture';

/**
 * Flutter derives its "Popular Locations" tiles from the loaded public
 * properties (property_provider.dart:115-135 — top 4 cities by listing count).
 * That needs the whole dataset, not the one page the featured query fetches,
 * so this port ships a fixed set of the markets the app actually serves and
 * links each into the browse page's existing `search` filter.
 */
const POPULAR_LOCATIONS = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret'] as const;

export function LandingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [heroSearch, setHeroSearch] = useState('');
  const [heroType, setHeroType] = useState<PropertyType | ''>('');

  const query = useQuery({
    queryKey: ['public-properties', 'featured'],
    queryFn: () => listPublicProperties({ page: 1 }),
  });

  const featured = (query.data?.results ?? []).slice(0, 3);

  function handleHeroSearch(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const params = new URLSearchParams();
    if (heroSearch !== '') params.set('search', heroSearch);
    if (heroType !== '') params.set('property_type', heroType);
    const qs = params.toString();
    navigate(qs === '' ? ROUTES.publicProperties : `${ROUTES.publicProperties}?${qs}`);
  }

  return (
    <>
      <section className="landing__hero">
        <p className="landing__eyebrow">Pam Golding Rent Management</p>
        <h1>Rent Management</h1>
        <p className="landing__lede">Property, tenancy and rent collection management.</p>
        <p className="landing__actions">
          <Link to={ROUTES.login} className="button button--primary">
            Sign in
          </Link>
          <Link to={ROUTES.register} className="button button--outlined">
            Create an account
          </Link>
        </p>
      </section>

      <section className="hero-search">
        <form
          className="search-bar"
          role="search"
          aria-label="Property search"
          onSubmit={handleHeroSearch}
        >
          <div className="search-bar__field">
            <label htmlFor="hero-search-type">Property type</label>
            <select
              id="hero-search-type"
              value={heroType}
              onChange={(event) => setHeroType(event.target.value as PropertyType | '')}
            >
              <option value="">Any type</option>
              {PROPERTY_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="search-bar__field">
            <label htmlFor="hero-search-location">City or keyword</label>
            <input
              id="hero-search-location"
              placeholder="e.g. Nairobi, Westlands…"
              value={heroSearch}
              onChange={(event) => setHeroSearch(event.target.value)}
            />
          </div>

          <button type="submit">Search</button>
        </form>
      </section>

      {/* Ports lifestyle_categories_section.dart. Flutter tiled the four most
          common property types from loaded data; every type is offered here so
          the section does not depend on what page 1 happens to contain. */}
      <section className="tile-section">
        <p className="landing__eyebrow">Lifestyle Collection</p>
        <h2>Explore by Lifestyle</h2>
        <ul className="tile-grid">
          {PROPERTY_TYPES.map((type) => (
            <li key={type}>
              <Link
                className="lifestyle-tile"
                to={`${ROUTES.publicProperties}?property_type=${type}`}
              >
                {type}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Ports locations_section.dart. */}
      <section className="tile-section">
        <p className="landing__eyebrow">Elite Neighborhoods</p>
        <h2>Popular Locations</h2>
        <ul className="tile-grid">
          {POPULAR_LOCATIONS.map((city) => (
            <li key={city}>
              <Link
                className="location-tile"
                to={`${ROUTES.publicProperties}?search=${encodeURIComponent(city)}`}
              >
                {city}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* TODO(feature phase): featured agents section — no agents/public/-shaped
          endpoint exists on either backend yet. */}
      {featured.length > 0 && (
        <section>
          <h2>Featured properties</h2>
          <ul className="featured-properties">
            {featured.map((property) => {
              const fromPrice = deriveFromPrice(property.units);
              const bedroomRange = deriveBedroomRange(property.units);
              const bathroomRange = deriveBathroomRange(property.units);
              const totalSquareFeet = deriveTotalSquareFeet(property.units);
              return (
                <li key={property.id} className="featured-property-card">
                  {property.primary_image?.image != null ? (
                    <PropertyPicture
                      className="featured-property-card__thumb"
                      src={property.primary_image.image}
                      alt=""
                      size="medium"
                    />
                  ) : (
                    <span
                      className="featured-property-card__thumb thumb--placeholder"
                      aria-hidden="true"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      No photo
                    </span>
                  )}
                  <div className="featured-property-card__body">
                    <div className="featured-property-card__heading">
                      <h3>
                        <Link to={`${ROUTES.publicProperties}/${property.id}`}>{property.name}</Link>
                      </h3>
                      {property.whatsapp_number !== '' && (
                        <a
                          className="property-card__whatsapp"
                          href={`https://wa.me/${property.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(
                            `Hi, I'm interested in ${property.name} (${property.address}). Is it still available?`,
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Message ${property.name} on WhatsApp`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MessageCircle size={18} aria-hidden="true" />
                        </a>
                      )}
                    </div>

                    <p className="featured-property-card__location">
                      <MapPin size={14} aria-hidden="true" />
                      {property.address}, {property.city}, {property.state}
                    </p>

                    {property.description !== '' && (
                      <p className="featured-property-card__description">{property.description}</p>
                    )}

                    <ul className="spec-row">
                      <li className="spec-row__item">
                        <Building2 size={16} aria-hidden="true" />
                        <span>{property.property_type}</span>
                      </li>
                      {bedroomRange !== null && (
                        <li className="spec-row__item">
                          <BedDouble size={16} aria-hidden="true" />
                          <span>{formatBedroomRange(bedroomRange)}</span>
                        </li>
                      )}
                      {bathroomRange !== null && (
                        <li className="spec-row__item">
                          <Bath size={16} aria-hidden="true" />
                          <span>{formatBathroomRange(bathroomRange)}</span>
                        </li>
                      )}
                      {totalSquareFeet !== null && (
                        <li className="spec-row__item">
                          <Maximize size={16} aria-hidden="true" />
                          <span>{totalSquareFeet.toLocaleString('en-KE')} sq ft</span>
                        </li>
                      )}
                      <li className="spec-row__item">
                        <DoorOpen size={16} aria-hidden="true" />
                        <span>
                          {property.vacant_units_count} / {property.total_units} vacant
                        </span>
                      </li>
                    </ul>

                    {property.features.length > 0 && (
                      <ul className="chip-row">
                        {property.features.slice(0, 5).map((feature) => (
                          <li key={feature} className="chip">
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="featured-property-card__footer">
                      <p className="badge-row">
                        {property.vacant_units_count > 0 && (
                          <span className="badge badge--available">Vacant units available</span>
                        )}
                        {isRecentlyListed(property.created_at) && (
                          <span className="badge badge--new">New</span>
                        )}
                      </p>
                      {fromPrice !== null ? (
                        <p className="listing-price">From {formatKes(fromPrice)}</p>
                      ) : property.vacant_units_count === 0 ? (
                        <p className="listing-price listing-price--muted">Fully occupied</p>
                      ) : (
                        <span />
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </>
  );
}

export default LandingPage;
